import _ from 'underscore';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';

import { constructSchemaI18n } from './schema-i18n-loader';
import dataStaticModelImport from './data-static-model';

import { ComponentI18nShim } from './component-i18n-shim';
import { mapLocaleToLcid, mapLocaleWebpack } from './map-locales';
import { I18nModelClone } from './i18n-model-clone';
import { ModuleI18nShim } from './module-i18n-shim';

const DataStaticModel = dataStaticModelImport;

const translateLegacyToIANATimezone =
  legacyTimeZone => toIANATimeZone(fromLegacyTimeZone(legacyTimeZone));
const mapLocale = mapLocaleWebpack;

function computeDataPath({
  loaderName,
  i18nName,
  overrideLocale,
}) {
  const cultureToLoad = mapLocale(overrideLocale);
  let dataPath;

  if (loaderName === 'module/extension/requirejs/i18n') {
    dataPath = `module/i18n/${i18nName}/data/${cultureToLoad}`;
  } else {
    let dataFolderPath = `data/i18n/${i18nName}`;

    // fetch legacy data file from legacy place
    if (i18nName.indexOf('legacy/') === 0) {
      dataFolderPath = `module/i18n/${i18nName.substring(7)}/data`;
    }

    dataPath = `${dataFolderPath}/${cultureToLoad}`;
  }

  return dataPath;
}


function $dataPathFor(i18nName, overrideLocale) {
  const lcidToUse = mapLocaleToLcid(overrideLocale);
  const dataPath = `component/data/static/${i18nName}/${lcidToUse}`;

  return dataPath;
}

export class LegacyI18nLoader {
  constructor({
    registry,
  }) {
    this.main = null; // Initialized in private\ui-next\packages\ui-next\src\app.js
    this.registry = registry;
  }

  setContext(serverContext) {
    const culture = serverContext.CurrentLocale;
    // CurrentUser / CurrentAccount can be absent in pre-account flows (e.g. the
    // accept-invitation page, where the authenticated invitee has not been
    // associated with a customer/account yet). Guard against null so the legacy
    // i18n bootstrap does not throw in those contexts.
    const lcid = serverContext.CurrentUser ? serverContext.CurrentUser.LocaleID : undefined;
    const {
      CurrentAccount,
    } = serverContext;
    const {
      CurrencyCode,
      TimeZone,
    } = CurrentAccount || {};

    this.context = {
      culture,
      lcid,
      CurrentAccount,
      CurrencyCode,
      TimeZone: TimeZone ? translateLegacyToIANATimezone(TimeZone) : undefined,
    };
  }

  setMain(mainI18n) {
    this.main = mainI18n;
  }


  loadI18nData({
    loaderName,
    i18nName,
    overrideLocale,
  }) {
    const dataPath = computeDataPath({
      loaderName,
      i18nName,
      overrideLocale,
    });

    return this.registry.loadDataPathWebpack({ dataPath });
  }

  loadStaticData({
    i18nName,
    overrideLocale,
  }) {
    const dataPath = $dataPathFor(i18nName, overrideLocale);
    const data = this.registry.loadDataPathWebpack({ dataPath });
    const { lcid } = this.context;

    return new DataStaticModel({ data, lcid });
  }

  loadSchemaI18n({ i18nName }) {
    const i18n = this.get(`component/i18n/loader!component/schema-i18n-${i18nName}`);

    return constructSchemaI18n({
      _,
      i18n,
    });
  }

  cloneI18n({
    // i18nName,
    i18nToClone,
    I18nModelCloneClass = I18nModelClone,
    legacyI18nData,
    overrideLocale,
    overrideTimeZone,
    overrideCurrency,
  }) {
    const mainClone = new I18nModelCloneClass({
      i18nToClone,
      newTranslationData: legacyI18nData,
      overrideLocale,
      overrideTimeZone,
      overrideCurrency,
    });

    return mainClone;
  }

  loadNormalI18n({
    loaderName,
    i18nName,
    overrideLocale,
    overrideTimeZone,
    overrideCurrency,
    i18nToClone,
  }) {
    const legacyI18nData = this.loadI18nData({
      loaderName,
      i18nName,
      overrideLocale,
    });

    const mainClone = this.cloneI18n({
      i18nName,
      i18nToClone,
      legacyI18nData,
      overrideLocale,
      overrideTimeZone,
      overrideCurrency,
    });

    if (loaderName === 'component/i18n/loader') {
      return new ComponentI18nShim(mainClone);
    } else if (loaderName === 'module/extension/requirejs/i18n') {
      return new ModuleI18nShim(mainClone);
    }

    return mainClone;
  }

  get(fullName) {
    const [loaderName, dataName] = fullName.split('!');
    const {
      culture,
      TimeZone,
      CurrencyCode,
    } = this.context;
    const [
      i18nName,
      overrideLocale = culture,
      overrideTimeZone = TimeZone,
      overrideCurrency = CurrencyCode,
    ] = dataName.split(':');

    if (loaderName === '$/data') {
      return this.loadStaticData({ i18nName, overrideLocale });
    }

    if (loaderName === 'component/schema-i18n/loader' || loaderName === '$/schema-i18n') {
      return this.loadSchemaI18n({ i18nName });
    }

    return this.loadNormalI18n({
      loaderName,
      i18nName,
      overrideLocale,
      overrideTimeZone,
      overrideCurrency,
      i18nToClone: this.main,
    });
  }
}
